import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message as antdMessage,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import type {
  MemberSource,
  SpaceGroup,
  SpaceGroupSyncSummary,
  SpaceMember,
  SpaceRole,
} from '@lens/shared';
import { SPACE_ROLES } from '@lens/shared';
import { ApiError } from '../../../shared/api/client.ts';
import {
  addMembers,
  linkSpaceGroups,
  listMembers,
  listSpaceGroups,
  removeMember,
  syncSpaceGroups,
  unlinkSpaceGroup,
  updateMemberRole,
  updateSpaceGroupRole,
} from '../api.ts';
import { GrantAccessModal } from './GrantAccessModal.tsx';
import { ROLE_LABEL, SKIP_REASON_LABEL } from './labels.ts';
import styles from '../SpaceSettingsPage.module.css';

/**
 * Вкладка «Участники»: кто имеет доступ к пространству и откуда.
 *
 * Доступ бывает двух видов. Ручной — админ добавил человека сам. Групповой —
 * человек состоит в группе Keycloak, привязанной к пространству; такие
 * строки пересчитываются автоматически и вручную не правятся (роль меняют
 * у группы, человека убирают в Keycloak).
 */

export interface MembersTabProps {
  spaceId: string;
  /**
   * Роль текущего пользователя. Управление доступом — только у админа
   * пространства: сервер и так вернёт 403, но кнопка, которая всегда падает,
   * хуже отсутствующей.
   */
  myRole: SpaceRole;
}

type SourceFilter = 'all' | MemberSource;

const SOURCE_FILTER_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: 'all', label: 'Любой источник' },
  { value: 'manual', label: 'Добавлен вручную' },
  { value: 'group', label: 'Из группы' },
];

/** Короткий человекочитаемый итог пересчёта — в toast. */
function summaryText(summary: SpaceGroupSyncSummary): string {
  const parts: string[] = [];
  if (summary.added > 0) parts.push(`добавлено: ${summary.added}`);
  if (summary.updated > 0) parts.push(`изменено ролей: ${summary.updated}`);
  if (summary.removed > 0) parts.push(`убрано: ${summary.removed}`);
  if (summary.usersCreated > 0) parts.push(`заведено в Lens: ${summary.usersCreated}`);
  return parts.length > 0 ? parts.join(', ') : 'состав не изменился';
}

export function MembersTab({ spaceId, myRole }: MembersTabProps): JSX.Element {
  const canManage = myRole === 'admin';
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [groups, setGroups] = useState<SpaceGroup[]>([]);
  const [keycloakConfigured, setKeycloakConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<SpaceRole | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMembers(spaceId);
      setMembers(res.members);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить участников');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  const loadGroups = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await listSpaceGroups(spaceId);
      setGroups(res.groups);
      setKeycloakConfigured(res.keycloakConfigured);
    } catch {
      // Привязка групп — дополнительная возможность: её недоступность не
      // должна прятать сам список участников.
      setGroups([]);
    }
  }, [spaceId, canManage]);

  useEffect(() => {
    void loadMembers();
    void loadGroups();
  }, [loadMembers, loadGroups]);

  // ─── Ручные участники ──────────────────────────────────────────────────

  const handleRoleChange = async (userId: string, role: SpaceRole): Promise<void> => {
    try {
      const updated = await updateMemberRole(spaceId, userId, { role });
      setMembers((prev) =>
        prev.map((m) => (m.userId === userId ? { ...m, role: updated.role } : m)),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось изменить роль');
    }
  };

  /**
   * Даёт человеку личную роль вместо той, что пришла из группы Keycloak.
   * Строка становится ручной: пересчёт состава группы её больше не трогает,
   * а доступ остаётся, даже если человека из группы уберут.
   */
  const pinRole = async (userId: string, role: SpaceRole): Promise<void> => {
    try {
      await addMembers(spaceId, { userIds: [userId], role });
      await loadMembers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось изменить роль');
    }
  };

  const confirmRoleChange = (member: SpaceMember, newRole: SpaceRole): void => {
    if (member.role === newRole) return;

    const displayName = member.displayName || member.login;
    const nextRoleLabel = ROLE_LABEL[newRole];
    const isAdminDemotion = member.role === 'admin' && newRole !== 'admin';
    const fromGroup = member.source === 'group';

    Modal.confirm({
      title: isAdminDemotion
        ? `Понизить администратора ${displayName} до ${nextRoleLabel}? Он потеряет доступ к настройкам.`
        : `Изменить роль участника ${displayName} на ${nextRoleLabel}?`,
      ...(fromGroup
        ? {
            content: `Сейчас роль приходит из группы ${member.sourceGroup?.name ?? 'Keycloak'}. После изменения она станет личной и обновление состава группы её больше не изменит.`,
          }
        : {}),
      okText: 'Изменить',
      cancelText: 'Отмена',
      ...(isAdminDemotion ? { okButtonProps: { danger: true } } : {}),
      onOk: async () => {
        if (fromGroup) {
          await pinRole(member.userId, newRole);
        } else {
          await handleRoleChange(member.userId, newRole);
        }
      },
    });
  };

  const handleRemove = async (userId: string): Promise<void> => {
    try {
      await removeMember(spaceId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить участника');
    }
  };

  // ─── Выдача доступа ────────────────────────────────────────────────────

  const handleAddUsers = useCallback(
    async (input: { userIds: string[]; keycloakIds: string[]; role: SpaceRole }) => {
      const { added, skipped, createdUsers } = await addMembers(spaceId, {
        ...(input.userIds.length > 0 ? { userIds: input.userIds } : {}),
        ...(input.keycloakIds.length > 0 ? { keycloakIds: input.keycloakIds } : {}),
        role: input.role,
      });

      // Перечитываем список целиком: часть строк могла превратиться из
      // групповых в ручные, а часть людей — только что завестись.
      await loadMembers();

      if (added.length > 0) {
        antdMessage.success(
          added.length === 1
            ? `Добавлен 1 участник${createdUsers > 0 ? ' (заведён в Lens)' : ''}`
            : `Добавлено участников: ${added.length}` +
                (createdUsers > 0 ? `, заведено в Lens: ${createdUsers}` : ''),
        );
      }
      if (skipped.length > 0) {
        const detail = [...new Set(skipped.map((s) => SKIP_REASON_LABEL[s.reason]))].join(', ');
        antdMessage.warning(`Пропущено: ${skipped.length} (${detail})`);
      }
    },
    [spaceId, loadMembers],
  );

  const handleLinkGroups = useCallback(
    async (input: { keycloakIds: string[]; role: SpaceRole }) => {
      const res = await linkSpaceGroups(spaceId, input);
      setGroups(res.groups);
      setMembers(res.members);
      antdMessage.success(`Группа привязана — ${summaryText(res.summary)}`);
      for (const warning of res.summary.warnings) antdMessage.warning(warning);
    },
    [spaceId],
  );

  // ─── Группы ────────────────────────────────────────────────────────────

  const handleSync = async (): Promise<void> => {
    setSyncing(true);
    setError(null);
    try {
      const res = await syncSpaceGroups(spaceId);
      setGroups(res.groups);
      setMembers(res.members);
      antdMessage.success(`Состав обновлён — ${summaryText(res.summary)}`);
      for (const warning of res.summary.warnings) antdMessage.warning(warning);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось обновить состав');
    } finally {
      setSyncing(false);
    }
  };

  const handleGroupRole = async (group: SpaceGroup, role: SpaceRole): Promise<void> => {
    if (group.role === role) return;
    Modal.confirm({
      title: `Дать всем из «${group.path}» роль «${ROLE_LABEL[role]}»?`,
      okText: 'Изменить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const res = await updateSpaceGroupRole(spaceId, group.id, { role });
          setGroups(res.groups);
          setMembers(res.members);
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Не удалось изменить роль группы');
        }
      },
    });
  };

  const handleUnlink = (group: SpaceGroup): void => {
    Modal.confirm({
      title: `Отвязать группу «${group.path}»?`,
      content: 'Люди, получившие доступ по этой группе, потеряют его. Добавленные вручную останутся.',
      okText: 'Отвязать',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const res = await unlinkSpaceGroup(spaceId, group.id);
          setGroups(res.groups);
          setMembers(res.members);
          antdMessage.success(
            res.summary.removed > 0
              ? `Группа отвязана, убрано участников: ${res.summary.removed}`
              : 'Группа отвязана',
          );
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Не удалось отвязать группу');
        }
      },
    });
  };

  // ─── Фильтры ───────────────────────────────────────────────────────────

  const visibleMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (sourceFilter !== 'all' && m.source !== sourceFilter) return false;
      if (!query) return true;
      return (
        m.login.toLowerCase().includes(query) ||
        m.displayName.toLowerCase().includes(query) ||
        (m.email ?? '').toLowerCase().includes(query)
      );
    });
  }, [members, search, roleFilter, sourceFilter]);

  const columns: ColumnsType<SpaceMember> = [
    {
      title: 'Логин',
      dataIndex: 'login',
      key: 'login',
    },
    {
      title: 'Имя',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (displayName: string, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Avatar size={24} src={record.avatarUrl ?? undefined}>
            {(displayName || record.login || '?').slice(0, 1).toUpperCase()}
          </Avatar>
          {displayName}
        </span>
      ),
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      render: (email: string | null) => email ?? '—',
    },
    {
      title: 'Доступ',
      key: 'source',
      width: 220,
      render: (_, record) => {
        if (record.source !== 'group') {
          return <Tag data-testid={`member-source-${record.userId}`}>вручную</Tag>;
        }
        const group = record.sourceGroup;
        // Привязка покрывает и подгруппы: показываем ту, где человек реально
        // состоит, а в подсказке — через какую привязку он сюда попал.
        const memberPath = group?.memberPath ?? group?.path ?? null;
        const nested = Boolean(group && memberPath && memberPath !== group.path);
        const label = nested
          ? (memberPath!.split('/').filter(Boolean).pop() ?? group!.name)
          : (group?.name ?? 'группа');
        return (
          <Tooltip
            title={
              nested
                ? `${memberPath} — по привязанной группе ${group!.path}`
                : (memberPath ?? 'Группа Keycloak')
            }
          >
            <Tag icon={<TeamOutlined />} color="blue" data-testid={`member-source-${record.userId}`}>
              {label}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      render: (role: SpaceRole, record) =>
        !canManage ? (
          <Typography.Text>{ROLE_LABEL[role]}</Typography.Text>
        ) : record.source === 'group' ? (
          <Tooltip title="Роль пришла из группы. Выберите другую — она станет личной и перестанет зависеть от группы.">
            <Select
              value={role}
              style={{ minWidth: 160 }}
              onChange={(newRole) => confirmRoleChange(record, newRole)}
              options={SPACE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
              data-testid={`member-role-select-${record.userId}`}
            />
          </Tooltip>
        ) : (
          <Select
            value={role}
            style={{ minWidth: 160 }}
            onChange={(newRole) => confirmRoleChange(record, newRole)}
            options={SPACE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
            data-testid={`member-role-select-${record.userId}`}
          />
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      hidden: !canManage,
      render: (_, record) =>
        record.source === 'group' ? (
          <Tooltip title="Доступ выдан группой — уберите человека из группы в Keycloak или отвяжите группу">
            <Button size="small" danger icon={<DeleteOutlined />} disabled>
              Удалить
            </Button>
          </Tooltip>
        ) : (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => void handleRemove(record.userId)}
            data-testid={`member-remove-${record.userId}`}
          >
            Удалить
          </Button>
        ),
    },
  ];

  const groupColumns: ColumnsType<SpaceGroup> = [
    {
      title: 'Группа',
      key: 'group',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{record.name}</Typography.Text>
          <Typography.Text type="secondary">{record.path}</Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Роль',
      key: 'role',
      width: 200,
      render: (_, record) => (
        <Select
          value={record.role}
          style={{ minWidth: 160 }}
          onChange={(role) => void handleGroupRole(record, role)}
          options={SPACE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
          data-testid={`space-group-role-${record.id}`}
        />
      ),
    },
    {
      title: 'Людей',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 90,
    },
    {
      title: 'Обновлено',
      key: 'lastSyncedAt',
      width: 200,
      render: (_, record) =>
        record.lastSyncError ? (
          <Tooltip title={record.lastSyncError}>
            <Tag color="red">ошибка обновления</Tag>
          </Tooltip>
        ) : record.lastSyncedAt ? (
          new Date(record.lastSyncedAt).toLocaleString('ru-RU')
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Button
          size="small"
          danger
          icon={<DisconnectOutlined />}
          onClick={() => handleUnlink(record)}
          data-testid={`space-group-unlink-${record.id}`}
        >
          Отвязать
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {error ? (
        <Alert type="error" message={error} showIcon data-testid="members-error" />
      ) : null}

      <Card className={styles.formCard}>
        <Space
          wrap
          style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}
        >
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Логин, имя или почта"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 260 }}
              data-testid="members-search"
            />
            <Select<SpaceRole | 'all'>
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: 180 }}
              options={[
                { value: 'all', label: 'Любая роль' },
                ...SPACE_ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] })),
              ]}
              data-testid="members-role-filter"
            />
            <Select<SourceFilter>
              value={sourceFilter}
              onChange={setSourceFilter}
              style={{ width: 190 }}
              options={SOURCE_FILTER_OPTIONS}
              data-testid="members-source-filter"
            />
          </Space>
          {canManage ? (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setModalOpen(true)}
              data-testid="members-grant-access"
            >
              Выдать доступ
            </Button>
          ) : null}
        </Space>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }} data-testid="members-loading">
            <Spin />
          </div>
        ) : (
          <div data-testid="members-table">
            <Table<SpaceMember>
              rowKey="userId"
              columns={columns}
              dataSource={visibleMembers}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      members.length === 0
                        ? 'Пока никого нет'
                        : 'Под фильтры никто не подходит'
                    }
                  />
                ),
              }}
            />
          </div>
        )}
      </Card>

      {canManage ? (
      <Card
        className={styles.formCard}
        title={
          <span>
            <TeamOutlined style={{ marginRight: 6 }} />
            Группы Keycloak
          </span>
        }
        extra={
          groups.length > 0 ? (
            <Button
              icon={<ReloadOutlined />}
              loading={syncing}
              onClick={() => void handleSync()}
              data-testid="space-groups-sync"
            >
              Обновить состав
            </Button>
          ) : null
        }
        data-testid="space-groups-card"
      >
        {groups.length === 0 ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {keycloakConfigured
              ? 'Ни одна группа не привязана. Нажмите «Выдать доступ» → «Группы Keycloak», ' +
                'чтобы выдать доступ всему отделу сразу — состав будет обновляться сам.'
              : 'Вход через Keycloak не настроен, поэтому выдать доступ группе нельзя.'}
          </Typography.Paragraph>
        ) : (
          <Table<SpaceGroup>
            rowKey="id"
            size="small"
            columns={groupColumns}
            dataSource={groups}
            pagination={false}
          />
        )}
      </Card>
      ) : null}

      {canManage ? (
      <GrantAccessModal
        open={modalOpen}
        spaceId={spaceId}
        members={members}
        keycloakConfigured={keycloakConfigured}
        onClose={() => setModalOpen(false)}
        onAddUsers={handleAddUsers}
        onLinkGroups={handleLinkGroups}
      />
      ) : null}
    </Space>
  );
}
